import {DragEndEvent} from "@dnd-kit/core";
import {
  Cell,
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  OnChangeFn,
  Row,
  RowData,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import {ReactNode, useMemo, useState} from "react";

import {SelectOption} from "@/components/select/Select.types.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {TableCrown} from "@/components/table/subComponents/TableCrown/TableCrown.tsx";
import {TableMarkup} from "@/components/table/subComponents/TableMarkup/TableMarkup.tsx";
import {TableRowExpander} from "@/components/table/subComponents/TableRowExpander/TableRowExpander.tsx";
import {
  ColumnSpec,
  Sorting,
  SortingDirection,
  specToDef,
  TableVariant,
  TextAlignment,
  toInnerSorting,
} from "@/components/table/Table.types.ts";

import {TableSelectionCheckBox} from "./subComponents/TableSelectionCheckBox/TableSelectionCheckBox.tsx";

import styles from "./Table.module.scss";

// https://tanstack.com/table/v8/docs/api/core/column-def
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    columnClassName?: string;
    headerClassName?: string;
    headerTextAlignment?: TextAlignment;
    allowPointerEvents?: boolean;
    onClick?: (e: React.MouseEvent, cell: Cell<TData, TValue> | undefined) => void;
  }
}

export type TableProps<T> = {
  rows: T[];
  indicatorColumnSpec?: ColumnSpec<T>;
  specs: ColumnSpec<T>[];
  onSortingChange?: (sorting: Sorting) => void;
  sorting?: Sorting;
  actionBar?: ReactNode;
  selectionSummary?: ReactNode;
  headerClassName?: string;
  className?: string;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: OnChangeFn<Record<string, boolean>>;
  subrowRenderer?: (row: T) => ReactNode;
  expandableSubRows?: boolean;
  fixedWidth?: never;
  minWidth?: never;
  noDataComponent?: ReactNode;
  tableRowOnClick?: (row: Row<T>) => void;
  tableRowClassName?: string;
  dataTestId?: string;
  subRowEndSpacer?: boolean;
  isDragAndDrop?: boolean;
  onDragEnd?: (event: DragEndEvent) => void;
  expandedDefault?: ExpandedState;
  isLoading?: boolean;
  fillContainerHeight?: boolean;
  variant?: TableVariant;
  placeholder?: boolean;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
  hidePaginationOptions?: boolean;
  paginationData?: unknown[];
} & Omit<TableOptions<T>, "onSortingChange" | "columns" | "data" | "getCoreRowModel">;

interface TablePropsFluid<T> extends Omit<TableProps<T>, "fixedWidth" | "minWidth"> {
  /*
   * We've made minWidth required when a table is fixed with as
   * Firefox doesn't properly handle min-width on table elements,
   * leading to column headers overlapping when shrunk down
   */
  fixedWidth: false;
  minWidth: undefined;
}

interface TablePropsFixed<T> extends Omit<TableProps<T>, "fixedWidth" | "minWidth"> {
  fixedWidth: true;
  minWidth: string;
}

interface TablePropsScrollable<T> extends Omit<TableProps<T>, "fixedWidth" | "minWidth"> {
  fixedWidth?: false;
  minWidth: string;
}

export const Table = <T,>({
  rows: data,
  specs,
  onSortingChange,
  sorting,
  manualSorting = false,
  actionBar,
  selectionSummary,
  enableRowSelection,
  rowSelection,
  setRowSelection,
  indicatorColumnSpec,
  subrowRenderer,
  headerClassName,
  className,
  expandableSubRows = false,
  fixedWidth = false,
  minWidth,
  noDataComponent,
  tableRowOnClick,
  tableRowClassName,
  dataTestId,
  subRowEndSpacer,
  getRowId,
  isDragAndDrop = false,
  onDragEnd,
  isLoading = false,
  fillContainerHeight = false,
  variant = "default",
  placeholder = false,
  expandedDefault,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
  hidePaginationOptions,
  paginationData,
  ...tableOptions
}: TableProps<T> | TablePropsFluid<T> | TablePropsFixed<T> | TablePropsScrollable<T>) => {
  const [expanded, setExpanded] = useState<ExpandedState>(expandedDefault || {});

  const isRowSelectionEnabled: boolean = !!setRowSelection;

  // `columns` must keep a stable reference across renders.
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<T>();
    const cols = specs.map((s) => specToDef(columnHelper, s));

    if (expandableSubRows && subrowRenderer) {
      cols.splice(0, 0, {
        id: "expander",
        cell: ({row}) => {
          if (!row) {
            return null;
          }
          return <TableRowExpander row={row} />;
        },
        meta: {
          columnClassName: "table-cell-content fit-content",
          headerClassName: "table-cell-content fit-content",
          allowPointerEvents: true,
        },
      });
    }

    // If you ever need to modify this row selection again I think we should move it out of here and make it a column spec that can be added to any table. This will make it easier to modify.
    if (isRowSelectionEnabled) {
      cols.splice(0, 0, {
        id: "select",
        header: ({table}) => (
          <TableSelectionCheckBox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({row}) => {
          return (
            <TableSelectionCheckBox
              className={row.depth > 0 ? styles.nestedRowSelection : undefined}
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          );
        },
        meta: {
          columnClassName: "table-cell-content fit-content cursor-default",
          headerClassName: "table-cell-content fit-content",
          allowPointerEvents: true,
        },
      });
    }

    if (indicatorColumnSpec) {
      return [specToDef(columnHelper, indicatorColumnSpec), ...cols];
    }

    return cols;
  }, [specs, expandableSubRows, subrowRenderer, isRowSelectionEnabled, indicatorColumnSpec]);

  const table = useReactTable<T>({
    ...tableOptions,
    data,
    getRowCanExpand: () => expandableSubRows || false,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getRowId: getRowId ? getRowId : undefined,
    state: {
      rowSelection,
      sorting: toInnerSorting(sorting),
      expanded,
    },
    manualSorting: manualSorting,
    enableRowSelection: enableRowSelection ?? isRowSelectionEnabled,
    enableMultiRowSelection: isRowSelectionEnabled,
    onRowSelectionChange: setRowSelection,
    onSortingChange: (sortingFnOrArray) => {
      if (!onSortingChange) {
        return;
      }
      if (Array.isArray(sortingFnOrArray)) {
        onSortingChange({
          column: sortingFnOrArray[0].id,
          direction: sortingFnOrArray[0].desc ? SortingDirection.Desc : SortingDirection.Asc,
        });
        return;
      }

      const oldSorting = toInnerSorting(sorting);
      const newSorting = sortingFnOrArray(oldSorting);
      if (newSorting.length > 0) {
        onSortingChange({
          column: newSorting[0].id,
          direction: newSorting[0].desc ? SortingDirection.Desc : SortingDirection.Asc,
        });
      } else if (oldSorting.length > 0) {
        onSortingChange({
          column: oldSorting[0].id,
          direction: oldSorting[0].desc ? SortingDirection.Asc : SortingDirection.Desc,
        });
      }
    },
  });

  const hasActionBar = isRowSelectionEnabled || !!actionBar;
  const hasPaginatorFooter = pagination && totalRows !== undefined && updatePagination;

  return (
    <TableMarkup
      actionBar={
        isRowSelectionEnabled ? (
          <TableCrown
            selectedItemsCount={Object.keys(rowSelection ?? {}).length || 0}
            selectionSummary={selectionSummary}
          >
            {actionBar}
          </TableCrown>
        ) : (
          actionBar
        )
      }
      headerGroups={table.getHeaderGroups()}
      rowModel={table.getRowModel()}
      noDataComponent={noDataComponent}
      subrowRenderer={subrowRenderer}
      expandableSubRows={expandableSubRows}
      fixedWidth={fixedWidth}
      minWidth={minWidth}
      headerClassName={headerClassName}
      className={classNames(
        className
          ? className
          : {
              "rounded-top": !hasActionBar && variant === "default",
              "rounded-bottom": !hasPaginatorFooter && variant === "default",
            },
        isRowSelectionEnabled ? "selectable-rows" : ""
      )}
      tableRowOnClick={tableRowOnClick}
      tableRowClassName={tableRowClassName}
      dataTestId={dataTestId}
      subRowEndSpacer={subRowEndSpacer}
      isDragAndDrop={isDragAndDrop}
      onDragEnd={onDragEnd}
      isLoading={isLoading}
      fillContainerHeight={fillContainerHeight}
      variant={variant}
      placeholder={placeholder}
      numberOfColumns={table.getAllColumns().length}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationOptions={paginationOptions}
      hidePaginationOptions={hidePaginationOptions}
      paginationData={paginationData}
    />
  );
};
